import {useMemo} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import {
  QUERY_CACHE_KEYS,
  OFFER_CATEGORIES,
  getSearchParamFromUrl,
  type Offer,
  type Paginated,
} from '@chekin/core';
import {API} from '@chekin/api';
import {isLuggageStorageEnabled} from 'utils/upselling';
import {isFirstLoad} from 'utils/query';

const CACHE_TIME = 60_000;
const STALE_TIME = 60_000;
const PAGE_SIZE = 24;

type UseInfiniteOffersParams = {
  housingId?: string;
};

function useInfiniteOffers({housingId}: UseInfiniteOffersParams) {
  const {
    data,
    isLoading,
    isFetched,
    isSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<Paginated<Offer>, Error>({
    queryKey: [QUERY_CACHE_KEYS.offers, 'infinite', housingId],
    queryFn: ({pageParam}) => {
      const params = new URLSearchParams();
      params.append('housing_id', housingId!);
      params.append('is_active', 'true');
      params.append('is_exclusive', 'false');
      params.append('page', String(pageParam));
      params.append('page_size', String(PAGE_SIZE));

      return API.upselling.getOffers(params.toString());
    },
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage?.next ? Number(getSearchParamFromUrl('page', lastPage.next)) : undefined,
    enabled: Boolean(housingId),
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });

  const offers = useMemo(() => {
    const loaded = data?.pages.flatMap(page => page.results) ?? [];

    if (isLuggageStorageEnabled()) {
      return loaded;
    }
    return loaded.filter(offer => offer.category !== OFFER_CATEGORIES.luggageServices);
  }, [data]);

  return {
    offers,
    isLoading: isFirstLoad({isLoading, isFetched}),
    isSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}

export {useInfiniteOffers};
